import { ChartLineArea, Earth, Left, NewspaperFolding, Right, Star } from '@icon-park/react'
import { BrandResponse } from '@shared/models'
import { useEffect, useMemo, useRef, useState } from 'react'
import { BrandTab, TabType } from '../../types'
import {
  BrandPagerButton,
  BrandTabsPaginationContainer,
  BrandTabsTrack,
  BrandTabsViewport,
  StatusDot,
  TabButton,
  TabsContainer,
} from './PromptHeader.styles'

interface Props {
  activeTab: TabType
  onTabChange: (tab: TabType) => void
  excludedDataSources?: number[]
  brands?: BrandResponse[]
  activeBrand?: string | null
  brandCompletion?: Record<string, boolean>
  dataSourceCompletion?: {
    social: boolean
    press: boolean
  }
}

const DATA_SOURCE_IDS = {
  SOCIAL_MEDIA: 1,
  PRESS: 2,
} as const

export function PromptHeader({
  activeTab,
  onTabChange,
  excludedDataSources = [],
  brands = [],
  activeBrand,
  brandCompletion = {},
  dataSourceCompletion = {
    social: false,
    press: false,
  },
}: Props): JSX.Element {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [itemsPerPage, setItemsPerPage] = useState(1)
  const [currentPage, setCurrentPage] = useState(0)
  const MIN_TAB_WIDTH = 210
  const TAB_GAP = 8

  const isSocialDisabled = excludedDataSources.includes(DATA_SOURCE_IDS.SOCIAL_MEDIA)
  const isPressDisabled = excludedDataSources.includes(DATA_SOURCE_IDS.PRESS)

  const brandTabs: BrandTab[] = brands
    .map((brand) => ({
      id: brand.id,
      name: brand.name,
      isMainBrand: Boolean(brand.mainBrand),
    }))
    .sort((a, b) =>
      a.isMainBrand === b.isMainBrand ? 0
      : a.isMainBrand ? -1
      : 1,
    )

  const totalPages = Math.max(1, Math.ceil(brandTabs.length / itemsPerPage))
  const shouldShowPager = totalPages > 1
  const activeBrandIndex = useMemo(
    () => brandTabs.findIndex((brand) => brand.id === activeBrand),
    [activeBrand, brandTabs],
  )

  useEffect(() => {
    const element = viewportRef.current
    if (!element) return

    const calculateItemsPerPage = () => {
      const width = element.clientWidth
      const calculated = Math.max(1, Math.floor((width + TAB_GAP) / (MIN_TAB_WIDTH + TAB_GAP)))
      setItemsPerPage(calculated)
    }

    calculateItemsPerPage()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', calculateItemsPerPage)
      return () => window.removeEventListener('resize', calculateItemsPerPage)
    }

    const observer = new ResizeObserver(calculateItemsPerPage)
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages - 1))
  }, [totalPages])

  useEffect(() => {
    if (activeBrandIndex < 0) return
    const pageForActiveBrand = Math.floor(activeBrandIndex / itemsPerPage)
    setCurrentPage((prev) => (pageForActiveBrand !== prev ? pageForActiveBrand : prev))
  }, [activeBrandIndex, itemsPerPage])

  const pageStart = currentPage * itemsPerPage
  const visibleBrandTabs = brandTabs.slice(pageStart, pageStart + itemsPerPage)

  const handleTabClick = (tab: TabType) => {
    if (tab === 'social' && isSocialDisabled) return
    if (tab === 'press' && isPressDisabled) return
    onTabChange(tab)
  }

  const renderDataSourceTabs = () => {
    if (!activeBrand) return null

    return (
      <>
        <TabButton
          $active={activeTab === 'social'}
          $disabled={isSocialDisabled}
          onClick={() => handleTabClick('social')}
          title={isSocialDisabled ? 'Esta fonte de dados está excluída da classificação de favorabilidade' : ''}>
          <Earth size={16} />
          Redes Sociais
          <StatusDot $success={dataSourceCompletion.social} />
          {isSocialDisabled && ' (Excluída)'}
        </TabButton>

        <TabButton
          $active={activeTab === 'press'}
          $disabled={isPressDisabled}
          onClick={() => handleTabClick('press')}
          title={isPressDisabled ? 'Esta fonte de dados está excluída da classificação de favorabilidade' : ''}>
          <NewspaperFolding size={16} />
          Imprensa
          <StatusDot $success={dataSourceCompletion.press} />
          {isPressDisabled && ' (Excluída)'}
        </TabButton>
      </>
    )
  }

  return (
    <>
      <BrandTabsPaginationContainer>
        <BrandPagerButton
          type="button"
          onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
          disabled={!shouldShowPager || currentPage === 0}
          aria-label="Página anterior de marcas"
          title="Página anterior de marcas">
          <Left size={14} />
        </BrandPagerButton>

        <BrandTabsViewport ref={viewportRef}>
          <BrandTabsTrack>
            {visibleBrandTabs.map((brandTab) => (
              <TabButton
                key={brandTab.id}
                $active={activeBrand === brandTab.id}
                $fluid={true}
                onClick={() => onTabChange(brandTab.id)}
                title={`Configurar prompt rules para ${brandTab.name}`}>
                <Star size={16} />
                {brandTab.name}
                <StatusDot $success={brandCompletion[brandTab.id] ?? false} />
                {brandTab.isMainBrand && ' (Principal)'}
              </TabButton>
            ))}
          </BrandTabsTrack>
        </BrandTabsViewport>

        <BrandPagerButton
          type="button"
          onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
          disabled={!shouldShowPager || currentPage >= totalPages - 1}
          aria-label="Próxima página de marcas"
          title="Próxima página de marcas">
          <Right size={14} />
        </BrandPagerButton>
      </BrandTabsPaginationContainer>

      {activeBrand && (
        <TabsContainer>
          {renderDataSourceTabs()}
          <TabButton $active={activeTab === 'test'} onClick={() => handleTabClick('test')}>
            <ChartLineArea size={16} />
            Testar Classificação
          </TabButton>
        </TabsContainer>
      )}
    </>
  )
}
